import React, { useState, useEffect } from 'react';
import { Database, Cpu, Brain, Zap, Shield, ArrowRight, CheckCircle, X } from 'lucide-react';

interface PipelineStage {
  id: number;
  name: string;
  icon: React.FC<{ className?: string }>;
  status: 'active' | 'processing' | 'ready';
  description: string;
  processes: string[];
  metrics: { label: string; value: string }[];
}

interface SignalData {
  ingestion: {
    eventsPerSec: number;
    awsStatus: 'green' | 'yellow' | 'red';
    csStatus: 'green' | 'yellow' | 'red';
    oktaStatus: 'green' | 'yellow' | 'red';
    queueDepth: number;
    latencyMs: number;
  };
  ml: {
    anomalyMean: number;
    anomalyStdDev: number;
    zScoreThreshold: number;
    isolationDepth: number;
    mahalanobis: number;
    lofScore: number;
    featureEntropy: number;
    pcaVariance: number;
    klDivergence: number;
    f1Score: number;
  };
  dnn: {
    softmaxConf: number;
    crossEntropy: number;
    attentionWeight: number;
    embeddingDist: number;
    gradientNorm: number;
    layerActivation: number;
    dropoutRate: number;
    batchSize: number;
    mitreCoverage: number;
    topKAgreement: number;
  };
  genai: {
    apiLatency: number;
    tokenUsage: number;
    contextUsed: number;
    ragSimilarity: number;
    retrievalHits: number;
    commandsGen: number;
  };
  execution: {
    actionsToday: number;
    successRate: number;
    awsActions: number;
    oktaActions: number;
    csActions: number;
    pendingApprovals: number;
  };
}

const AIPipeline: React.FC = () => {
  const [selectedStage, setSelectedStage] = useState<PipelineStage | null>(null);
  const [stageData, setStageData] = useState<Record<number, number[]>>({});
  const [signals, setSignals] = useState<SignalData>({
    ingestion: { eventsPerSec: 1247, awsStatus: 'green', csStatus: 'green', oktaStatus: 'green', queueDepth: 23, latencyMs: 45 },
    ml: { anomalyMean: 0.42, anomalyStdDev: 0.18, zScoreThreshold: 2.5, isolationDepth: 8.3, mahalanobis: 3.2, lofScore: 1.1, featureEntropy: 4.7, pcaVariance: 0.89, klDivergence: 0.08, f1Score: 0.94 },
    dnn: { softmaxConf: 0.92, crossEntropy: 0.24, attentionWeight: 0.78, embeddingDist: 0.15, gradientNorm: 1.2, layerActivation: 0.67, dropoutRate: 0.1, batchSize: 32, mitreCoverage: 0.73, topKAgreement: 0.88 },
    genai: { apiLatency: 1.8, tokenUsage: 847, contextUsed: 0.42, ragSimilarity: 0.84, retrievalHits: 12, commandsGen: 3 },
    execution: { actionsToday: 47, successRate: 0.985, awsActions: 28, oktaActions: 12, csActions: 7, pendingApprovals: 0 }
  });

  const stages: PipelineStage[] = [
    { id: 1, name: 'Data Ingestion', icon: Database, status: 'active', description: 'Multi-source security data collection and normalization',
      processes: ['AWS GuardDuty API polling (30s intervals)', 'CrowdStrike Falcon Event Stream', 'Okta System Log ingestion', 'Data normalization & enrichment', 'Deduplication engine', 'Event correlation mapping'],
      metrics: [{ label: 'Events/sec', value: '847' }, { label: 'Sources Active', value: '3/3' }, { label: 'Queue Depth', value: '124' }, { label: 'Latency', value: '45ms' }]
    },
    { id: 2, name: 'ML Analysis', icon: Cpu, status: 'active', description: 'Real-time anomaly detection using ensemble ML models',
      processes: ['Isolation Forest anomaly detection', 'Behavioral baseline comparison', 'Statistical outlier analysis', 'Time-series pattern matching', 'Feature extraction pipeline', 'Anomaly score calculation'],
      metrics: [{ label: 'Model Accuracy', value: '94.2%' }, { label: 'Predictions/sec', value: '1,247' }, { label: 'False Positive Rate', value: '2.1%' }, { label: 'Avg Score Time', value: '12ms' }]
    },
    { id: 3, name: 'DNN Classification', icon: Brain, status: 'processing', description: 'Deep neural network threat classification with MITRE mapping',
      processes: ['Multi-layer threat classification', 'MITRE ATT&CK technique mapping', 'Confidence score calculation', 'Threat family identification', 'Kill chain stage detection', 'Severity assessment'],
      metrics: [{ label: 'Classification Acc', value: '97.8%' }, { label: 'MITRE Coverage', value: '156 techniques' }, { label: 'Inference Time', value: '28ms' }, { label: 'Model Version', value: 'v2.4.1' }]
    },
    { id: 4, name: 'GenAI + RAG', icon: Zap, status: 'ready', description: 'Arbitium AI-powered response formulation with knowledge retrieval',
      processes: ['Context assembly from threat data', 'RAG knowledge base retrieval', 'AI engine request formation', 'Remediation action generation', 'Command syntax validation', 'Rollback procedure creation'],
      metrics: [{ label: 'Avg Response', value: '1.8s' }, { label: 'Token Usage', value: '~850/req' }, { label: 'Success Rate', value: '99.2%' }, { label: 'Model', value: 'Arbitium v1' }]
    },
    { id: 5, name: 'Execution Engine', icon: Shield, status: 'ready', description: 'Automated remediation execution across security platforms',
      processes: ['Action validation & approval check', 'AWS remediation (EC2, IAM, S3)', 'Okta user lifecycle actions', 'CrowdStrike host containment', 'Execution logging & audit trail', 'Rollback capability maintenance'],
      metrics: [{ label: 'Actions Executed', value: '47 today' }, { label: 'Success Rate', value: '98.5%' }, { label: 'Avg Exec Time', value: '340ms' }, { label: 'Rollbacks', value: '0' }]
    },
  ];

  // Update signals with realistic fluctuations
  useEffect(() => {
    const interval = setInterval(() => {
      setSignals(prev => ({
        ingestion: {
          eventsPerSec: Math.max(800, Math.min(1800, prev.ingestion.eventsPerSec + (Math.random() - 0.5) * 200)),
          awsStatus: Math.random() > 0.02 ? 'green' : 'yellow',
          csStatus: Math.random() > 0.02 ? 'green' : 'yellow',
          oktaStatus: Math.random() > 0.02 ? 'green' : 'yellow',
          queueDepth: Math.max(0, Math.min(100, prev.ingestion.queueDepth + (Math.random() - 0.5) * 20)),
          latencyMs: Math.max(20, Math.min(100, prev.ingestion.latencyMs + (Math.random() - 0.5) * 15)),
        },
        ml: {
          anomalyMean: Math.max(0.1, Math.min(0.9, prev.ml.anomalyMean + (Math.random() - 0.5) * 0.1)),
          anomalyStdDev: Math.max(0.05, Math.min(0.4, prev.ml.anomalyStdDev + (Math.random() - 0.5) * 0.05)),
          zScoreThreshold: 2.5,
          isolationDepth: Math.max(5, Math.min(12, prev.ml.isolationDepth + (Math.random() - 0.5) * 1)),
          mahalanobis: Math.max(1, Math.min(8, prev.ml.mahalanobis + (Math.random() - 0.5) * 0.8)),
          lofScore: Math.max(0.8, Math.min(2, prev.ml.lofScore + (Math.random() - 0.5) * 0.2)),
          featureEntropy: Math.max(3, Math.min(7, prev.ml.featureEntropy + (Math.random() - 0.5) * 0.5)),
          pcaVariance: Math.max(0.7, Math.min(0.98, prev.ml.pcaVariance + (Math.random() - 0.5) * 0.05)),
          klDivergence: Math.max(0.01, Math.min(0.3, prev.ml.klDivergence + (Math.random() - 0.5) * 0.03)),
          f1Score: Math.max(0.85, Math.min(0.99, prev.ml.f1Score + (Math.random() - 0.5) * 0.02)),
        },
        dnn: {
          softmaxConf: Math.max(0.7, Math.min(0.99, prev.dnn.softmaxConf + (Math.random() - 0.5) * 0.05)),
          crossEntropy: Math.max(0.1, Math.min(0.5, prev.dnn.crossEntropy + (Math.random() - 0.5) * 0.05)),
          attentionWeight: Math.max(0.5, Math.min(0.95, prev.dnn.attentionWeight + (Math.random() - 0.5) * 0.08)),
          embeddingDist: Math.max(0.05, Math.min(0.4, prev.dnn.embeddingDist + (Math.random() - 0.5) * 0.05)),
          gradientNorm: Math.max(0.5, Math.min(2.5, prev.dnn.gradientNorm + (Math.random() - 0.5) * 0.3)),
          layerActivation: Math.max(0.3, Math.min(0.9, prev.dnn.layerActivation + (Math.random() - 0.5) * 0.1)),
          dropoutRate: 0.1,
          batchSize: 32,
          mitreCoverage: Math.max(0.6, Math.min(0.85, prev.dnn.mitreCoverage + (Math.random() - 0.5) * 0.03)),
          topKAgreement: Math.max(0.7, Math.min(0.98, prev.dnn.topKAgreement + (Math.random() - 0.5) * 0.05)),
        },
        genai: {
          apiLatency: Math.max(0.8, Math.min(3.5, prev.genai.apiLatency + (Math.random() - 0.5) * 0.4)),
          tokenUsage: Math.max(500, Math.min(1200, prev.genai.tokenUsage + (Math.random() - 0.5) * 100)),
          contextUsed: Math.max(0.2, Math.min(0.7, prev.genai.contextUsed + (Math.random() - 0.5) * 0.08)),
          ragSimilarity: Math.max(0.6, Math.min(0.98, prev.genai.ragSimilarity + (Math.random() - 0.5) * 0.05)),
          retrievalHits: Math.max(5, Math.min(25, Math.round(prev.genai.retrievalHits + (Math.random() - 0.5) * 4))),
          commandsGen: Math.max(1, Math.min(8, Math.round(prev.genai.commandsGen + (Math.random() - 0.5) * 2))),
        },
        execution: {
          actionsToday: prev.execution.actionsToday + (Math.random() > 0.9 ? 1 : 0),
          successRate: Math.max(0.95, Math.min(1, prev.execution.successRate + (Math.random() - 0.5) * 0.01)),
          awsActions: prev.execution.awsActions + (Math.random() > 0.92 ? 1 : 0),
          oktaActions: prev.execution.oktaActions + (Math.random() > 0.95 ? 1 : 0),
          csActions: prev.execution.csActions + (Math.random() > 0.97 ? 1 : 0),
          pendingApprovals: Math.max(0, Math.min(5, Math.round(prev.execution.pendingApprovals + (Math.random() - 0.6) * 1))),
        }
      }));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Generate activity data for charts
  useEffect(() => {
    const generateData = () => {
      const data: Record<number, number[]> = {};
      stages.forEach(stage => {
        data[stage.id] = Array.from({ length: 15 }, () => Math.random() * 100);
      });
      setStageData(data);
    };
    generateData();
    const interval = setInterval(() => {
      setStageData(prev => {
        const newData: Record<number, number[]> = {};
        stages.forEach(stage => {
          const old = prev[stage.id] || [];
          newData[stage.id] = [...old.slice(1), Math.random() * 100];
        });
        return newData;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 border-green-500/50 text-green-400';
      case 'processing': return 'bg-arbitium-primary/20 border-arbitium-primary/50 text-arbitium-primary animate-pulse';
      case 'ready': return 'bg-arbitium-accent/20 border-arbitium-accent/50 text-arbitium-accent';
      default: return 'bg-gray-500/20 border-gray-500/50 text-gray-400';
    }
  };

  const StatusDot: React.FC<{ status: 'green' | 'yellow' | 'red' }> = ({ status }) => (
    <span className={`w-2 h-2 rounded-full ${status === 'green' ? 'bg-green-500' : status === 'yellow' ? 'bg-yellow-500 animate-pulse' : 'bg-red-500 animate-pulse'}`} />
  );

  const MiniBar: React.FC<{ value: number; max?: number; color?: string }> = ({ value, max = 100, color = 'bg-arbitium-primary' }) => (
    <div className="w-12 h-1.5 bg-arbitium-darker rounded-full overflow-hidden">
      <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${(value / max) * 100}%` }} />
    </div>
  );

  const SignalValue: React.FC<{ value: number | string; unit?: string; good?: 'high' | 'low'; threshold?: number }> = ({ value, unit = '', good, threshold }) => {
    let colorClass = 'text-white';
    if (good && threshold !== undefined) {
      const numVal = typeof value === 'number' ? value : parseFloat(value as string);
      if (good === 'high') colorClass = numVal >= threshold ? 'text-green-400' : 'text-yellow-400';
      else colorClass = numVal <= threshold ? 'text-green-400' : 'text-yellow-400';
    }
    return <span className={`font-mono text-xs ${colorClass}`}>{typeof value === 'number' ? value.toFixed(2) : value}{unit}</span>;
  };

  return (
    <>
      <div className="bg-arbitium-dark/50 backdrop-blur-xl border border-arbitium-primary/10 rounded-xl p-4 h-full">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">AI Analysis Pipeline</h2>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 text-xs text-green-400">
              <CheckCircle className="w-3 h-3" /> Pipeline Health:
            </span>
            <span className="text-xs font-medium text-green-400">Optimal</span>
          </div>
        </div>

        {/* Pipeline Stages */}
        <div className="flex items-start justify-between gap-1">
          {stages.map((stage, index) => (
            <React.Fragment key={stage.id}>
              <div className="flex flex-col items-center flex-1">
                {/* Stage Icon */}
                <div 
                  className={`w-14 h-14 rounded-xl border-2 flex items-center justify-center mb-2 transition-all cursor-pointer hover:scale-105 ${getStatusColor(stage.status)}`}
                  onClick={() => setSelectedStage(stage)}
                >
                  <stage.icon className="w-6 h-6" />
                </div>
                <p className="text-xs font-medium text-white text-center mb-2">{stage.name}</p>

                {/* Signal Grid for each stage */}
                <div className="w-full bg-arbitium-darker/70 rounded-lg p-2 space-y-1.5 text-[10px]">
                  {stage.id === 1 && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Events/s</span>
                        <span className="font-mono text-green-400">{Math.round(signals.ingestion.eventsPerSec)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">AWS</span>
                        <StatusDot status={signals.ingestion.awsStatus} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">CS</span>
                        <StatusDot status={signals.ingestion.csStatus} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Okta</span>
                        <StatusDot status={signals.ingestion.oktaStatus} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Queue</span>
                        <MiniBar value={signals.ingestion.queueDepth} color={signals.ingestion.queueDepth > 70 ? 'bg-yellow-500' : 'bg-green-500'} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Latency</span>
                        <span className={`font-mono ${signals.ingestion.latencyMs < 50 ? 'text-green-400' : 'text-yellow-400'}`}>{Math.round(signals.ingestion.latencyMs)}ms</span>
                      </div>
                    </>
                  )}
                  {stage.id === 2 && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">μ(score)</span>
                        <SignalValue value={signals.ml.anomalyMean} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">σ(score)</span>
                        <SignalValue value={signals.ml.anomalyStdDev} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Z-thresh</span>
                        <span className="font-mono text-arbitium-accent">{signals.ml.zScoreThreshold}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">iForest</span>
                        <SignalValue value={signals.ml.isolationDepth} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Mahalan.</span>
                        <SignalValue value={signals.ml.mahalanobis} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">LOF</span>
                        <SignalValue value={signals.ml.lofScore} good="low" threshold={1.5} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Entropy</span>
                        <SignalValue value={signals.ml.featureEntropy} unit="b" />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">PCA var</span>
                        <SignalValue value={signals.ml.pcaVariance} good="high" threshold={0.85} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">KL div</span>
                        <SignalValue value={signals.ml.klDivergence} good="low" threshold={0.15} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">F1</span>
                        <SignalValue value={signals.ml.f1Score} good="high" threshold={0.9} />
                      </div>
                    </>
                  )}
                  {stage.id === 3 && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Softmax</span>
                        <SignalValue value={signals.dnn.softmaxConf} good="high" threshold={0.85} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">X-Entropy</span>
                        <SignalValue value={signals.dnn.crossEntropy} good="low" threshold={0.3} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Attention</span>
                        <MiniBar value={signals.dnn.attentionWeight * 100} color="bg-arbitium-primary" />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Embed dist</span>
                        <SignalValue value={signals.dnn.embeddingDist} good="low" threshold={0.25} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">∇ norm</span>
                        <SignalValue value={signals.dnn.gradientNorm} good="low" threshold={1.5} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Activation</span>
                        <MiniBar value={signals.dnn.layerActivation * 100} color="bg-arbitium-secondary" />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Dropout</span>
                        <span className="font-mono text-gray-400">{(signals.dnn.dropoutRate * 100).toFixed(0)}%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Batch</span>
                        <span className="font-mono text-white">{signals.dnn.batchSize}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">MITRE</span>
                        <SignalValue value={(signals.dnn.mitreCoverage * 100).toFixed(0)} unit="%" good="high" threshold={70} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Top-K</span>
                        <SignalValue value={signals.dnn.topKAgreement} good="high" threshold={0.8} />
                      </div>
                    </>
                  )}
                  {stage.id === 4 && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Latency</span>
                        <span className={`font-mono ${signals.genai.apiLatency < 2 ? 'text-green-400' : 'text-yellow-400'}`}>{signals.genai.apiLatency.toFixed(1)}s</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Tokens</span>
                        <span className="font-mono text-white">{Math.round(signals.genai.tokenUsage)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Context</span>
                        <MiniBar value={signals.genai.contextUsed * 100} color={signals.genai.contextUsed > 0.6 ? 'bg-yellow-500' : 'bg-green-500'} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">RAG sim</span>
                        <SignalValue value={signals.genai.ragSimilarity} good="high" threshold={0.75} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Retrievals</span>
                        <span className="font-mono text-arbitium-accent">{signals.genai.retrievalHits}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Commands</span>
                        <span className="font-mono text-white">{signals.genai.commandsGen}</span>
                      </div>
                    </>
                  )}
                  {stage.id === 5 && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Today</span>
                        <span className="font-mono text-green-400">{signals.execution.actionsToday}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Success</span>
                        <SignalValue value={(signals.execution.successRate * 100).toFixed(1)} unit="%" good="high" threshold={95} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">AWS</span>
                        <span className="font-mono text-orange-400">{signals.execution.awsActions}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Okta</span>
                        <span className="font-mono text-blue-400">{signals.execution.oktaActions}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">CS</span>
                        <span className="font-mono text-red-400">{signals.execution.csActions}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Pending</span>
                        <span className={`font-mono ${signals.execution.pendingApprovals > 0 ? 'text-yellow-400 animate-pulse' : 'text-green-400'}`}>{signals.execution.pendingApprovals}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              {index < stages.length - 1 && (
                <div className="flex items-center pt-6">
                  <ArrowRight className="w-4 h-4 text-arbitium-primary/50 flex-shrink-0" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="mt-3 pt-3 border-t border-arbitium-primary/10 flex items-center justify-between text-xs text-gray-500">
          <span>Throughput: <span className="text-white">{Math.round(signals.ingestion.eventsPerSec)} events/s</span></span>
          <span>E2E Latency: <span className="text-white">&lt;{(signals.ingestion.latencyMs / 1000 + signals.genai.apiLatency).toFixed(1)}s</span></span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
            <span className="text-green-400">Live</span>
          </span>
        </div>
      </div>

      {/* Stage Detail Modal */}
      {selectedStage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-arbitium-dark border border-arbitium-primary/20 rounded-2xl p-6 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl border-2 flex items-center justify-center ${getStatusColor(selectedStage.status)}`}>
                  <selectedStage.icon className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{selectedStage.name}</h2>
                  <p className="text-sm text-gray-400">{selectedStage.description}</p>
                </div>
              </div>
              <button onClick={() => setSelectedStage(null)} className="p-2 text-gray-400 hover:text-white hover:bg-arbitium-primary/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Activity Chart */}
            <div className="bg-arbitium-darker/50 border border-arbitium-primary/10 rounded-xl p-4 mb-6">
              <h3 className="text-sm font-medium text-gray-400 mb-3">Activity (Last 15 minutes)</h3>
              <div className="h-32">
                <svg viewBox="0 0 400 100" className="w-full h-full" preserveAspectRatio="none">
                  {[0, 25, 50, 75, 100].map(y => (
                    <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="#8B5CF620" strokeWidth="1" />
                  ))}
                  <defs>
                    <linearGradient id="stageGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  {stageData[selectedStage.id] && (
                    <>
                      <path
                        d={`M 0 100 ${stageData[selectedStage.id].map((val, i) => `L ${(i / (stageData[selectedStage.id].length - 1)) * 400} ${100 - val}`).join(' ')} L 400 100 Z`}
                        fill="url(#stageGradient)"
                      />
                      <path
                        d={stageData[selectedStage.id].map((val, i) => `${i === 0 ? 'M' : 'L'} ${(i / (stageData[selectedStage.id].length - 1)) * 400} ${100 - val}`).join(' ')}
                        fill="none" stroke="#8B5CF6" strokeWidth="2"
                      />
                    </>
                  )}
                </svg>
              </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              {selectedStage.metrics.map((metric, i) => (
                <div key={i} className="bg-arbitium-darker/50 border border-arbitium-primary/10 rounded-lg p-3">
                  <p className="text-xs text-gray-500">{metric.label}</p>
                  <p className="text-lg font-bold text-white">{metric.value}</p>
                </div>
              ))}
            </div>

            {/* Processes */}
            <div className="bg-arbitium-darker/50 border border-arbitium-primary/10 rounded-xl p-4">
              <h3 className="text-sm font-medium text-gray-400 mb-3">Active Processes</h3>
              <div className="space-y-2">
                {selectedStage.processes.map((process, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                    <span className="text-white">{process}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIPipeline;
